import * as React from 'react';

const PREVIEW_LOAD_TIMEOUT_MS = 20_000;

interface UseCheckoutPreviewFrameOptions {
  previewUrl: string | null;
  enabled: boolean;
}

/** Detect slow/blocked iframe loads — onError is unreliable for X-Frame-Options/CSP. */
export function useCheckoutPreviewFrame({ previewUrl, enabled }: UseCheckoutPreviewFrameOptions) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isBlocked, setIsBlocked] = React.useState(false);

  React.useEffect(() => {
    if (!enabled || !previewUrl) {
      setIsLoading(false);
      setIsBlocked(false);
      return;
    }

    setIsBlocked(false);
    setIsLoading(true);

    const timer = window.setTimeout(() => {
      setIsLoading((prev) => {
        if (prev) setIsBlocked(true);
        return false;
      });
    }, PREVIEW_LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [enabled, previewUrl]);

  const handleLoad = React.useCallback(() => {
    setIsLoading(false);
    setIsBlocked(false);
  }, []);

  const resetBlocked = React.useCallback(() => {
    setIsBlocked(false);
  }, []);

  return { isLoading, isBlocked, handleLoad, resetBlocked, setIsLoading };
}
